'use client';

import dynamic from 'next/dynamic';
import React from 'react';
import { PortalContextProvider } from '@/common/Portal/PortalContext';
import HomeData from '@/home/HomeData';
import {
  browserCookieIdExperiments,
  clientSideUserIdExperiments
} from '~/constants/home/experiments';
import AnalyticsProvider from '~/context/common/AnalyticsProvider';
import {
  DeviceContextProvider,
  type OwnProps as DeviceContextProps
} from '~/context/common/DeviceContext';
import { ErrorContextProvider } from '~/context/common/ErrorContext';
import ExperimentProvider, {
  type PropsFromServer as ExperimentContextProps
} from '~/context/common/ExperimentProvider';
import {
  NativeContextProvider,
  type OwnProps as NativeContextProps
} from '~/context/common/NativeContext';
import { SavedSearchesContextProvider } from '~/context/common/SavedSearchesContext';
import {
  StaticContextProvider,
  type OwnProps as StaticContextProps
} from '~/context/common/StaticContext';
import {
  TradeContextProvider,
  type OwnProps as TradeContextProps
} from '~/context/common/TradeContext';
import {
  UserContextProvider,
  type OwnProps as UserContextProps
} from '~/context/common/UserContext';
import {
  ComposerContextProvider,
  type OwnProps as ComposerContextProps
} from '~/context/composer/ComposerContext';
import { AvailabilityAndPriceContextProvider } from '~/context/home/AvailabilityAndPriceContext';
import { FavoritesContextProvider } from '~/context/home/FavoritesContext';
import { MegaEventContextProvider } from '~/context/home/MegaEventContext';
import importWithFallback from '~/utilities/importWithFallback';
import { FinanceContextProvider } from './FinanceContext';
import {
  GlobalContextProvider,
  type OwnProps as GlobalContextProps
} from './GlobalContext';
import {
  InternalLinkContextProvider,
  type OwnProps as InternalLinkContextProps
} from './InternalLinkContext';
import { ModalContextProvider } from './ModalContext';
import {
  RecentSearchesContextProvider,
  type OwnProps as RecentSearchesContextType
} from './RecentSearchesContext';
import { RecentVehiclesContextProvider } from './RecentVehiclesContext';
import { SuggestedForYouContextProvider } from './SuggestedForYouContext';
import { UserPurchaseContextProvider } from './UserPurchaseContext';
import {
  AcquisitionContextProvider,
  type OwnProps as AcquisitionContextProps
} from '../common/AcquisitionContext';

const CoreAuth = dynamic(
  () =>
    importWithFallback(
      () => import(/* webpackChunkName: "CoreAuth" */ '@/common/CoreAuth'),
      'CoreAuth'
    ),
  { ssr: false }
);

export type HomeProvidersProps = {
  forAcquisitionContext: AcquisitionContextProps;
  forAnalyticsContext: {
    correlationId: string;
    payloadMetaAttributes: {
      isReturningUser: boolean;
      isWebview: boolean;
      webview: {
        platform: 'ios' | 'android' | null;
        version: string;
      } | null;
    };
  };
  forComposerContext?: ComposerContextProps;
  forDeviceContext: DeviceContextProps;
  forExperimentContext: ExperimentContextProps;
  forGlobalContext: GlobalContextProps;
  forInternalLinkContext: InternalLinkContextProps;
  forNativeContext: NativeContextProps;
  forRecentSearchesContext: RecentSearchesContextType;
  forStaticContext: StaticContextProps;
  forTradeContext: TradeContextProps;
  forUserContext: UserContextProps;
};

type OwnProps = React.PropsWithChildren<
  HomeProvidersProps & {
    eventableProject: string;
    eventableTrack: string;
  }
>;

export default function HomeProviders({
  children,
  eventableProject,
  eventableTrack,
  forAcquisitionContext,
  forAnalyticsContext,
  forComposerContext,
  forDeviceContext,
  forExperimentContext,
  forGlobalContext,
  forInternalLinkContext,
  forNativeContext,
  forRecentSearchesContext,
  forStaticContext,
  forTradeContext,
  forUserContext
}: OwnProps) {
  // App Router gets ComposerContextProvider from the layout; only Pages Router passes forComposerContext.
  const content = (
    <NativeContextProvider {...forNativeContext}>
      <ExperimentProvider
        {...forExperimentContext}
        browserCookieIdExperiments={browserCookieIdExperiments}
        userIdExperiments={clientSideUserIdExperiments}
      >
        <ErrorContextProvider>
          <AnalyticsProvider
            projectName={eventableProject}
            track={eventableTrack}
            {...forAnalyticsContext}
          >
            <DeviceContextProvider {...forDeviceContext}>
              <GlobalContextProvider {...forGlobalContext}>
                <AcquisitionContextProvider {...forAcquisitionContext}>
                  <InternalLinkContextProvider {...forInternalLinkContext}>
                    <UserContextProvider {...forUserContext}>
                      <RecentSearchesContextProvider
                        {...forRecentSearchesContext}
                      >
                        <FinanceContextProvider>
                          <TradeContextProvider {...forTradeContext}>
                            <UserPurchaseContextProvider>
                              <FavoritesContextProvider>
                                <SavedSearchesContextProvider>
                                  <AvailabilityAndPriceContextProvider>
                                    <RecentVehiclesContextProvider>
                                      <SuggestedForYouContextProvider>
                                        <ModalContextProvider>
                                          <PortalContextProvider>
                                            <MegaEventContextProvider>
                                              <CoreAuth />
                                              <HomeData />
                                              {children}
                                            </MegaEventContextProvider>
                                          </PortalContextProvider>
                                        </ModalContextProvider>
                                      </SuggestedForYouContextProvider>
                                    </RecentVehiclesContextProvider>
                                  </AvailabilityAndPriceContextProvider>
                                </SavedSearchesContextProvider>
                              </FavoritesContextProvider>
                            </UserPurchaseContextProvider>
                          </TradeContextProvider>
                        </FinanceContextProvider>
                      </RecentSearchesContextProvider>
                    </UserContextProvider>
                  </InternalLinkContextProvider>
                </AcquisitionContextProvider>
              </GlobalContextProvider>
            </DeviceContextProvider>
          </AnalyticsProvider>
        </ErrorContextProvider>
      </ExperimentProvider>
    </NativeContextProvider>
  );

  return (
    <StaticContextProvider {...forStaticContext}>
      {forComposerContext ? (
        <ComposerContextProvider {...forComposerContext}>
          {content}
        </ComposerContextProvider>
      ) : (
        content
      )}
    </StaticContextProvider>
  );
}
